// src/pages/Dashboard/Admin/SeasonStats/hooks/useSeasonStatsDashboard.js

import {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";

import { toast } from "react-toastify";

import {
  addSeasonStat,
  addBreakdownsBulk,
  deleteSeasonStat,
  editBreakdownsBulk,
  editSeasonStat,
  generateSeasonStats,
  getSeasonStats,
  getSeasonStatsComparison,
} from "../../../../../services/seasonStatsServices";

import {
  calculateSeasonSummary,
  normalizeSeasonStat,
  sanitizeBreakdowns,
  sortSeasonStats,
} from "../utils/seasonStatsDashboardUtils";

export function useSeasonStatsDashboard() {
  const [seasons, setSeasons] = useState([]);
  const [comparison, setComparison] = useState([]);

  const [selectedSeasonId, setSelectedSeasonId] =
    useState(null);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [generating, setGenerating] =
    useState(false);
  const [deletingId, setDeletingId] =
    useState(null);

  const [editorOpen, setEditorOpen] =
    useState(false);

  const [editingSeason, setEditingSeason] =
    useState(null);

  const fetchDashboard = useCallback(
    async (preferredId = null) => {
      try {
        setLoading(true);

        const [seasonResponse, comparisonResponse] =
          await Promise.all([
            getSeasonStats(),
            getSeasonStatsComparison(),
          ]);

        const seasonRows = Array.isArray(
          seasonResponse,
        )
          ? seasonResponse
          : [];

        const normalized = sortSeasonStats(
          seasonRows.map(normalizeSeasonStat),
        );

        const comparisonRows = Array.isArray(
          comparisonResponse,
        )
          ? comparisonResponse
          : [];

        setSeasons(normalized);
        setComparison(comparisonRows);

        setSelectedSeasonId((currentId) => {
          if (
            preferredId &&
            normalized.some(
              (item) =>
                Number(item.id) ===
                Number(preferredId),
            )
          ) {
            return Number(preferredId);
          }

          if (
            currentId &&
            normalized.some(
              (item) =>
                Number(item.id) ===
                Number(currentId),
            )
          ) {
            return Number(currentId);
          }

          return normalized[0]?.id || null;
        });
      } catch (error) {
        console.error(
          "Erreur chargement statistiques saisons :",
          error,
        );

        toast.error(
          "Erreur lors du chargement des statistiques.",
        );

        setSeasons([]);
        setComparison([]);
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  useEffect(() => {
    fetchDashboard();
  }, [fetchDashboard]);

  const currentSeason = useMemo(() => {
    return (
      seasons.find(
        (item) =>
          Number(item.id) ===
          Number(selectedSeasonId),
      ) || null
    );
  }, [seasons, selectedSeasonId]);

  const summary = useMemo(() => {
    return calculateSeasonSummary(currentSeason);
  }, [currentSeason]);

  const openCreateEditor = () => {
    setEditingSeason(null);
    setEditorOpen(true);
  };

  const openEditEditor = () => {
    if (!currentSeason) {
      toast.error(
        "Aucune saison sélectionnée.",
      );
      return;
    }

    setEditingSeason(currentSeason);
    setEditorOpen(true);
  };

  const closeEditor = () => {
    if (saving) return;

    setEditorOpen(false);
    setEditingSeason(null);
  };

  const handleCreateSeason = async (
    seasonData,
    breakdowns,
  ) => {
    try {
      setSaving(true);

      const created = await addSeasonStat({
        season: seasonData.season,
        source: "MANUAL",
        snapshot_date:
          seasonData.snapshot_date || null,
        notes: seasonData.notes || null,
      });

      const seasonStatId = created?.id;

      if (!seasonStatId) {
        throw new Error(
          "La saison créée ne possède pas d'identifiant.",
        );
      }

      const cleanedBreakdowns =
        sanitizeBreakdowns(breakdowns);

      if (cleanedBreakdowns.length > 0) {
        await addBreakdownsBulk({
          season_stat_id: seasonStatId,
          breakdowns: cleanedBreakdowns,
        });
      }

      toast.success(
        "Statistiques de saison créées.",
      );

      setEditorOpen(false);
      setEditingSeason(null);

      await fetchDashboard(seasonStatId);
    } catch (error) {
      console.error(
        "Erreur création statistiques saison :",
        error,
      );

      toast.error(
        error?.response?.data?.message ||
          "Erreur lors de la création de la saison.",
      );
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateSeason = async (
    seasonId,
    seasonData,
    breakdowns,
  ) => {
    if (!seasonId) {
      toast.error(
        "Saison statistique introuvable.",
      );
      return;
    }

    try {
      setSaving(true);

      await editSeasonStat(seasonId, {
        season: seasonData.season,
        snapshot_date:
          seasonData.snapshot_date || null,
        notes: seasonData.notes || null,
      });

      await editBreakdownsBulk(
        seasonId,
        sanitizeBreakdowns(breakdowns),
      );

      toast.success(
        "Statistiques mises à jour.",
      );

      setEditorOpen(false);
      setEditingSeason(null);

      await fetchDashboard(seasonId);
    } catch (error) {
      console.error(
        "Erreur modification statistiques saison :",
        error,
      );

      toast.error(
        error?.response?.data?.message ||
          "Erreur lors de la modification.",
      );
    } finally {
      setSaving(false);
    }
  };

  const handleGenerateSeason = async () => {
    try {
      setGenerating(true);

      let result;

      try {
        result = await generateSeasonStats({});
      } catch (error) {
        const status = error?.response?.status;
        const data = error?.response?.data;

        if (
          status === 409 &&
          data?.require_replace
        ) {
          const replace = window.confirm(
            `${data.message}\n\nVoulez-vous remplacer les statistiques existantes par un nouveau snapshot ?`,
          );

          if (!replace) {
            return;
          }

          result = await generateSeasonStats({
            replace: true,
          });
        } else {
          throw error;
        }
      }

      const seasonStatId =
        result?.season_stat?.id;

      toast.success(
        result?.message ||
          "Statistiques générées.",
      );

      await fetchDashboard(
        seasonStatId || null,
      );
    } catch (error) {
      console.error(
        "Erreur génération statistiques :",
        error,
      );

      toast.error(
        error?.response?.data?.message ||
          "Erreur lors de la génération des statistiques.",
      );
    } finally {
      setGenerating(false);
    }
  };

  const handleDeleteSeason = async () => {
    if (!currentSeason?.id) {
      toast.error(
        "Aucune saison sélectionnée.",
      );
      return;
    }

    const confirmed = window.confirm(
      `Supprimer définitivement les statistiques de la saison ${currentSeason.season} ?`,
    );

    if (!confirmed) return;

    try {
      setDeletingId(currentSeason.id);

      await deleteSeasonStat(
        currentSeason.id,
      );

      toast.success(
        "Statistiques supprimées.",
      );

      await fetchDashboard();
    } catch (error) {
      console.error(
        "Erreur suppression statistiques :",
        error,
      );

      toast.error(
        "Erreur lors de la suppression.",
      );
    } finally {
      setDeletingId(null);
    }
  };

  const refreshDashboard = useCallback(
    async () => {
      await fetchDashboard();
    },
    [fetchDashboard],
  );

  return {
    seasons,
    comparison,

    selectedSeasonId,
    setSelectedSeasonId,

    currentSeason,
    summary,

    loading,
    saving,
    generating,
    deletingId,

    editorOpen,
    editingSeason,

    openCreateEditor,
    openEditEditor,
    closeEditor,

    handleCreateSeason,
    handleUpdateSeason,
    handleGenerateSeason,
    handleDeleteSeason,

    refreshDashboard,
  };
}